import LocalStorageHelper from "models/helper/LocalStorageHelper";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";

const MOCK_LATENCY_IN_MS = 90;
const COMMENT_STATUS = {
    OPEN: "open",
    RESOLVED: "resolved",
};

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value));
}

function getDisplayNameForUser(userInfo) {
    if (!userInfo) {
        return "You";
    }
    const fullName = [userInfo.firstName, userInfo.lastName].filter(Boolean).join(" ").trim();
    return fullName || userInfo.fullName || userInfo.username || userInfo.email || "You";
}

function getInitials(nameOrEmail) {
    const fallback = (nameOrEmail || "T").trim();
    const parts = fallback.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return fallback.substring(0, 2).toUpperCase();
}

function withLatency(result) {
    return new Promise(function(resolve) {
        setTimeout(function() {
            resolve(cloneJson(result));
        }, MOCK_LATENCY_IN_MS);
    });
}

function isValidCommentStatus(status) {
    return status === COMMENT_STATUS.OPEN || status === COMMENT_STATUS.RESOLVED;
}

const TurboEditorCommentsAPI = function() {
    this._projectIdToComments = {};
    this._idCounter = 0;
};

TurboEditorCommentsAPI.prototype._createId = function(prefix) {
    this._idCounter = this._idCounter + 1;
    return prefix + "_" + Date.now() + "_" + this._idCounter;
};

TurboEditorCommentsAPI.prototype._getProjectKey = function(params) {
    params = params || {};
    return params.anchorProjectId || params.projectId || params.sequencerId || "local-draft-project";
};

TurboEditorCommentsAPI.prototype._shouldUseBackend = function(params) {
    params = params || {};
    const projectId = params.anchorProjectId || params.projectId;
    return !!(projectId && /^[0-9a-fA-F]{24}$/.test(projectId.toString()));
};

TurboEditorCommentsAPI.prototype._normalizeBackendParams = function(params) {
    params = Object.assign({}, params || {});
    if (params.anchorProjectId && !params.projectId) {
        params.projectId = params.anchorProjectId;
    }
    if (params.selectionAnchor && !params.selection) {
        params.selection = params.selectionAnchor;
    }
    return params;
};

TurboEditorCommentsAPI.prototype._getCurrentUser = function() {
    const userInfo = LocalStorageHelper.getInstance().getUserInfo();
    const name = getDisplayNameForUser(userInfo);
    return {
        userUID: userInfo && userInfo._id ? userInfo._id : "current-user",
        name: name,
        username: userInfo && userInfo.username ? userInfo.username : "you",
        email: userInfo && userInfo.email ? userInfo.email : "you@trebble.local",
        initials: getInitials(name),
        profileImageUrl: userInfo && userInfo.profileImageUrl ? userInfo.profileImageUrl : null,
    };
};

TurboEditorCommentsAPI.prototype._getMockWorkspaceMembers = function() {
    const currentUser = this._getCurrentUser();
    const baseMembers = [
        currentUser,
        {
            userUID: "mock-member-maya",
            name: "Maya Reviewer",
            username: "maya",
            email: "maya@example.com",
            initials: "MR",
            profileImageUrl: null,
        },
        {
            userUID: "mock-member-jules",
            name: "Jules Editor",
            username: "jules",
            email: "jules@example.com",
            initials: "JE",
            profileImageUrl: null,
        },
        {
            userUID: "mock-member-sam",
            name: "Sam Producer",
            username: "sam",
            email: "sam@example.com",
            initials: "SP",
            profileImageUrl: null,
        },
    ];

    const seen = {};
    return baseMembers.filter(function(member) {
        const key = (member.email || member.username || member.userUID || "").toLowerCase();
        if (!key || seen[key]) {
            return false;
        }
        seen[key] = true;
        return true;
    });
};

TurboEditorCommentsAPI.prototype._ensureProjectBucket = function(projectKey) {
    if (!this._projectIdToComments[projectKey]) {
        this._projectIdToComments[projectKey] = [];
    }
    return this._projectIdToComments[projectKey];
};

TurboEditorCommentsAPI.prototype.listComments = function(params) {
    if (this._shouldUseBackend(params)) {
        return TrebbleClientAPIHelper.getInstance().listTurboEditorComments(this._normalizeBackendParams(params));
    }
    const projectKey = this._getProjectKey(params);
    return withLatency({
        comments: this._ensureProjectBucket(projectKey),
        mentionCandidates: this._getMockWorkspaceMembers(),
    });
};

TurboEditorCommentsAPI.prototype.listMentionCandidates = function(params) {
    if (this._shouldUseBackend(params)) {
        return TrebbleClientAPIHelper.getInstance().listTurboEditorCommentMentionCandidates(this._normalizeBackendParams(params));
    }
    params = params || {};
    const query = (params.query || "").toLowerCase();
    const candidates = this._getMockWorkspaceMembers().filter(function(member) {
        return !query
            || (member.name && member.name.toLowerCase().indexOf(query) !== -1)
            || (member.username && member.username.toLowerCase().indexOf(query) !== -1)
            || (member.email && member.email.toLowerCase().indexOf(query) !== -1);
    });
    return withLatency({ mentionCandidates: candidates });
};

TurboEditorCommentsAPI.prototype.createComment = function(params) {
    params = params || {};
    if (this._shouldUseBackend(params)) {
        return TrebbleClientAPIHelper.getInstance().createTurboEditorComment(this._normalizeBackendParams(params));
    }
    const projectKey = this._getProjectKey(params);
    const now = Date.now();
    const currentUser = this._getCurrentUser();
    const comment = {
        id: this._createId("turbo_comment"),
        projectId: projectKey,
        status: COMMENT_STATUS.OPEN,
        selection: params.selection || params.selectionAnchor || null,
        selectionAnchor: params.selectionAnchor || params.selection || null,
        messages: [
            {
                id: this._createId("turbo_comment_message"),
                author: currentUser,
                body: params.body || "",
                mentions: params.mentions || [],
                createdAt: now,
            },
        ],
        createdAt: now,
        updatedAt: now,
        resolvedAt: null,
    };
    this._ensureProjectBucket(projectKey).unshift(comment);
    return withLatency({ comment: comment });
};

TurboEditorCommentsAPI.prototype.createReply = function(params) {
    params = params || {};
    if (this._shouldUseBackend(params)) {
        return TrebbleClientAPIHelper.getInstance().replyToTurboEditorComment(this._normalizeBackendParams(params));
    }
    const projectKey = this._getProjectKey(params);
    const comments = this._ensureProjectBucket(projectKey);
    const comment = comments.find(function(candidate) {
        return candidate.id === params.commentId;
    });
    if (!comment) {
        return Promise.reject(new Error("Comment not found"));
    }
    const now = Date.now();
    const message = {
        id: this._createId("turbo_comment_message"),
        author: this._getCurrentUser(),
        body: params.body || "",
        mentions: params.mentions || [],
        createdAt: now,
    };
    comment.messages.push(message);
    comment.updatedAt = now;
    return withLatency({ comment: comment, message: message });
};

TurboEditorCommentsAPI.prototype.updateComment = function(params) {
    params = params || {};
    if (this._shouldUseBackend(params)) {
        return TrebbleClientAPIHelper.getInstance().updateTurboEditorComment(this._normalizeBackendParams(params));
    }
    const projectKey = this._getProjectKey(params);
    const comments = this._ensureProjectBucket(projectKey);
    const comment = comments.find(function(candidate) {
        return candidate.id === params.commentId;
    });
    if (!comment) {
        return Promise.reject(new Error("Comment not found"));
    }
    const previousStatus = comment.status;
    const nextStatus = isValidCommentStatus(params.status) ? params.status : previousStatus;
    comment.status = nextStatus;
    comment.updatedAt = Date.now();
    if (previousStatus !== COMMENT_STATUS.RESOLVED && nextStatus === COMMENT_STATUS.RESOLVED) {
        comment.resolvedAt = comment.updatedAt;
    } else if (previousStatus === COMMENT_STATUS.RESOLVED && nextStatus !== COMMENT_STATUS.RESOLVED) {
        comment.resolvedAt = null;
    }
    return withLatency({ comment: comment });
};

const instance = new TurboEditorCommentsAPI();

export default {
    getInstance: function() {
        return instance;
    },
};
